import axios, { AxiosResponse } from 'axios';
import React, { createContext, ReactNode, useContext } from 'react';

import {
  Address,
  AddressCreationAttributes,
  AddressUpdateAttributes,
} from '../models/Address';
import api from '../services/api';

// Define the context type
interface AddressContextType {
  getUserAddresses: () => Promise<Address[]>;
  createAddress: (address: AddressCreationAttributes) => Promise<Address>;
  updateAddress: (
    id: string,
    address: AddressUpdateAttributes,
  ) => Promise<Address>;
  deleteAddress: (id: string) => Promise<void>;
  setDefaultAddress: (id: string) => Promise<Address>;
}

// Create the context
const AddressContext = createContext<AddressContextType | undefined>(undefined);

// Custom hook for using the AddressContext
export const useAddress = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddress must be used within an AddressProvider');
  }
  return context;
};

interface AddressProviderProps {
  children: ReactNode;
}

export const AddressProvider: React.FC<AddressProviderProps> = ({
  children,
}) => {
  const reactAppApiUrl = process.env.REACT_APP_API_URL;
  if (!reactAppApiUrl) {
    throw new Error('REACT_APP_API_URL is not set');
  }

  const addressesUrl = new URL('addresses/', reactAppApiUrl).toString();

  // Pobieranie adresów użytkownika
  const getUserAddresses = async (): Promise<Address[]> => {
    try {
      const response = await api.get(new URL(addressesUrl).toString());
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const response = error.response as AxiosResponse;
        throw new Error(
          response.data.message || 'Błąd podczas pobierania adresów',
        );
      }
      throw new Error(error.message || 'Błąd podczas pobierania adresów');
    }
  };

  // Tworzenie nowego adresu
  const createAddress = async (
    addressData: AddressCreationAttributes,
  ): Promise<Address> => {
    try {
      const response = await api.post(
        new URL(addressesUrl).toString(),
        addressData,
      );
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const response = error.response as AxiosResponse;
        throw new Error(
          response.data.message || 'Błąd podczas dodawania adresu',
        );
      }
      throw new Error(error.message || 'Błąd podczas dodawania adresu');
    }
  };

  // Aktualizacja adresu
  const updateAddress = async (
    id: string,
    addressData: AddressUpdateAttributes,
  ): Promise<Address> => {
    try {
      const response = await api.put(
        new URL(`${id}`, addressesUrl).toString(),
        addressData,
      );
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const response = error.response as AxiosResponse;
        throw new Error(
          response.data.message || 'Błąd podczas aktualizacji adresu',
        );
      }
      throw new Error(error.message || 'Błąd podczas aktualizacji adresu');
    }
  };

  // Usuwanie adresu
  const deleteAddress = async (id: string): Promise<void> => {
    try {
      await api.delete(new URL(`${id}`, addressesUrl).toString());
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const response = error.response as AxiosResponse;
        throw new Error(
          response.data.message || 'Błąd podczas usuwania adresu',
        );
      }
      throw new Error(error.message || 'Błąd podczas usuwania adresu');
    }
  };

  // Ustawianie adresu jako domyślnego
  const setDefaultAddress = async (id: string): Promise<Address> => {
    try {
      const response = await api.put(
        new URL(`${id}/default`, addressesUrl).toString(),
      );
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const response = error.response as AxiosResponse;
        throw new Error(
          response.data.message ||
            'Błąd podczas ustawiania adresu jako domyślnego',
        );
      }
      throw new Error(
        error.message || 'Błąd podczas ustawiania adresu jako domyślnego',
      );
    }
  };

  return (
    <AddressContext.Provider
      value={{
        getUserAddresses,
        createAddress,
        updateAddress,
        deleteAddress,
        setDefaultAddress,
      }}
    >
      {children}
    </AddressContext.Provider>
  );
};
